import React, { useRef } from "react";
import {
  Box, Button, TextField, Typography, Chip, CircularProgress,
  Tooltip, MenuItem, Select, FormControl,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useCreateSmsTemplateMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router";

// ── Variable definitions ──────────────────────────────────────────────────────
const VARIABLES = [
  { label: "Customer Name",    tag: "{customer_name}",    desc: "Full name of the customer" },
  { label: "Trip Date",        tag: "{trip_date}",         desc: "Scheduled trip date" },
  { label: "Pickup Time",      tag: "{pickup_time}",       desc: "Pickup time" },
  { label: "Drop-off Time",    tag: "{dropoff_time}",      desc: "Drop-off time" },
  { label: "Pickup Address",   tag: "{pickup_address}",    desc: "Pickup location" },
  { label: "Drop-off Address", tag: "{dropoff_address}",   desc: "Drop-off location" },
  { label: "Driver Name",      tag: "{driver_name}",       desc: "Assigned driver name" },
  { label: "Driver Phone",     tag: "{driver_phone}",      desc: "Assigned driver phone" },
  { label: "Bus Type",         tag: "{bus_type}",          desc: "Type/category of bus" },
  { label: "Passengers",       tag: "{passengers}",        desc: "Number of passengers" },
  { label: "Company Name",     tag: "{company_name}",      desc: "Your company name" },
  { label: "Quote Number",     tag: "{quote_number}",      desc: "Quote / work-order number" },
  { label: "Invoice Number",   tag: "{invoice_number}",    desc: "Invoice reference" },
  { label: "Amount Due",       tag: "{amount_due}",        desc: "Outstanding balance" },
  { label: "Payment Link",     tag: "{payment_link}",      desc: "Online payment URL" },
];

const smsInfo = (len: number) => {
  if (len === 0) return { parts: 1, limit: 160, remaining: 160, color: "#64748b" };
  if (len <= 160) return { parts: 1, limit: 160, remaining: 160 - len, color: len > 140 ? "#f59e0b" : "#16a34a" };
  const parts = Math.ceil(len / 153);
  const remaining = parts * 153 - len;
  return { parts, limit: parts * 153, remaining, color: remaining < 10 ? "#f59e0b" : "#ef4444" };
};

const Index = () => {
  const { showErrorMessage, showMessage } = useNotifier();
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [createTemplate, { isLoading }] = useCreateSmsTemplateMutation();

  const insertVariable = (tag: string) => {
    const el = textareaRef.current;
    if (el) {
      const start = el.selectionStart;
      const end   = el.selectionEnd;
      const current = formik.values.message;
      const next = current.slice(0, start) + tag + current.slice(end);
      formik.setFieldValue("message", next);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + tag.length, start + tag.length);
      }, 0);
    } else {
      formik.setFieldValue("message", formik.values.message + tag);
    }
  };

  const formik = useFormik({
    initialValues: { name: "", category: "client" as "client" | "driver" | "both", message: "" },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.name.trim()) errors.name = "Template name is required";
      if (!values.message.trim()) errors.message = "Message body is required";
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await createTemplate({
          name:     values.name,
          category: values.category,
          message:  values.message,
        }).unwrap();
        if (!res.status) { showErrorMessage(res.message); return; }
        showMessage("Template created successfully");
        resetForm();
        navigate("/sms/list");
      } catch { showErrorMessage("Something went wrong"); }
    },
  });

  const { parts, limit, remaining, color } = smsInfo(formik.values.message.length);
  const isOverLimit = formik.values.message.length > 160;

  const labelSx = { fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5, display: "block" };
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2, fontSize: 13, bgcolor: "#fff",
      "&:hover fieldset": { borderColor: "#0ea5e9" },
      "&.Mui-focused fieldset": { borderColor: "#0ea5e9", borderWidth: 2 },
    },
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          background: "linear-gradient(135deg,#0ea5e9,#2f3891)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <SmsOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>
            Create SMS Template
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
            Design a reusable SMS template for clients or drivers
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: { xs: "wrap", md: "nowrap" } }}>

        {/* ── Left: Variable panel ─────────────────────────────────────────── */}
        <Box sx={{
          width: { xs: "100%", md: 240 }, flexShrink: 0,
          bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden",
        }}>
          <Box sx={{ px: 2, py: 1.5, background: "linear-gradient(135deg,#0ea5e9,#2f3891)" }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#fff" }}>
              Dynamic Variables
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.75)", mt: 0.3 }}>
              Click to insert at cursor
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
            {VARIABLES.map((v) => (
              <Tooltip key={v.tag} title={v.desc} placement="right" arrow>
                <Box
                  onClick={() => insertVariable(v.tag)}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 1.25, py: 0.75, borderRadius: 1.5,
                    border: "1px solid #e2e8f0", cursor: "pointer",
                    transition: "all 0.12s",
                    "&:hover": { bgcolor: "#e0f2fe", borderColor: "#0ea5e9" },
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#1e293b" }}>{v.label}</Typography>
                    <Typography sx={{ fontSize: 9.5, color: "#0ea5e9", fontFamily: "monospace" }}>{v.tag}</Typography>
                  </Box>
                  <ContentCopyIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>

        {/* ── Right: Form ──────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
            <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Template Details</Typography>
            </Box>

            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Template Name */}
              <Box>
                <Typography sx={labelSx}>Template Name *</Typography>
                <TextField
                  fullWidth size="small"
                  placeholder="e.g. Trip Confirmation"
                  {...formik.getFieldProps("name")}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  sx={fieldSx}
                />
              </Box>

              {/* Category */}
              <Box>
                <Typography sx={labelSx}>Category *</Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={formik.values.category}
                    onChange={(e) => formik.setFieldValue("category", e.target.value)}
                    sx={{ borderRadius: 2, fontSize: 13, bgcolor: "#fff" }}
                  >
                    <MenuItem value="client">Client — send to customers</MenuItem>
                    <MenuItem value="driver">Driver — send to assigned drivers</MenuItem>
                    <MenuItem value="both">Both — send to customers and drivers</MenuItem>
                  </Select>
                </FormControl>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                  Determines who receives this message
                </Typography>
              </Box>

              {/* Message */}
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography sx={labelSx}>Message Body *</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {parts > 1 && (
                      <Chip
                        label={`${parts} SMS parts`}
                        size="small"
                        sx={{ height: 18, fontSize: 9.5, fontWeight: 600, bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" }}
                      />
                    )}
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color }}>
                      {formik.values.message.length}/{limit} ({remaining} left)
                    </Typography>
                  </Box>
                </Box>
                <TextField
                  fullWidth multiline rows={10}
                  inputRef={textareaRef}
                  placeholder={"Hi {customer_name}, your trip on {trip_date} at {pickup_time} is confirmed.\nPickup: {pickup_address}\nDriver: {driver_name} | {driver_phone}\nThank you - TJBus"}
                  {...formik.getFieldProps("message")}
                  error={(formik.touched.message && Boolean(formik.errors.message)) || isOverLimit}
                  helperText={
                    (formik.touched.message && formik.errors.message) ||
                    (parts > 1 ? `This message will be sent as ${parts} SMS parts (charged separately per recipient)` : undefined)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2, fontSize: 12.5, fontFamily: "monospace", lineHeight: 1.6, bgcolor: "#f8fafc",
                      "& fieldset": { borderColor: parts > 1 ? "#f59e0b" : "#e2e8f0" },
                      "&:hover fieldset": { borderColor: "#0ea5e9" },
                      "&.Mui-focused fieldset": { borderColor: "#0ea5e9" },
                    },
                    "& .MuiFormHelperText-root": { color: parts > 1 ? "#f59e0b" : undefined },
                  }}
                />

                {/* SMS segment indicator */}
                <Box sx={{ mt: 1, display: "flex", gap: 0.5, alignItems: "center" }}>
                  {Array.from({ length: Math.min(parts || 1, 5) }).map((_, i) => (
                    <Box key={i} sx={{ height: 4, width: 32, borderRadius: 2, bgcolor: i < parts ? "#0ea5e9" : "#e2e8f0" }} />
                  ))}
                  {parts > 5 && <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>+{parts - 5} more</Typography>}
                  <Typography sx={{ fontSize: 10.5, color: "#64748b", ml: 0.5 }}>
                    {parts <= 1 ? "1 SMS" : `${parts} SMS parts`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── Actions ──────────────────────────────────────────────────────── */}
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", mt: 2, pt: 2, borderTop: "1px solid #f1f5f9" }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/sms/list")}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13, borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveAltIcon />}
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
              sx={{
                borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13, px: 3,
                background: "linear-gradient(135deg,#0ea5e9,#2f3891)",
                boxShadow: "0 2px 8px rgba(14,165,233,0.35)",
                "&:hover": { background: "linear-gradient(135deg,#0284c7,#2f3891)" },
                "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
              }}
            >
              Save Template
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Index;
